import { defineStore } from 'pinia';
import { notificationApi } from '@/api/notification';
import { getMyUnreadNotificationCount } from '@/api/skillhub';
import { useUserStore } from './user';

export const useNotificationStore = defineStore('notification', () => {
  // 未读通知数量（YY 站内信 + XiaoyuClaw 自建通知之和）
  const unreadCount = ref(0);

  // 获取未读通知数量；返回是否有任一来源成功（供轮询失败退避使用）
  const fetchUnreadCount = async (): Promise<boolean> => {
    const userStore = useUserStore();
    // 如果没有 token，重置未读数量并直接返回
    if (!userStore.token) {
      unreadCount.value = 0;
      return true;
    }

    let total = 0;
    let failures = 0;
    try {
      const response = await notificationApi.getUnreadCount();
      if (response && response.success) {
        // 兼容 data 为 number 或 { data: number }
        const value = typeof response.data === 'number' ? response.data : response.data?.data;
        total += Number(value ?? 0);
      }
    }
    catch (error) {
      failures += 1;
      console.error('获取未读通知数量失败:', error);
    }

    // XiaoyuClaw notifications (automation run results etc.) share the same
    // badge; failures here must not break the YY count above.
    try {
      total += await getMyUnreadNotificationCount();
    }
    catch (error) {
      failures += 1;
      console.error('获取 XiaoyuClaw 未读通知数量失败:', error);
    }

    // Keep the previous badge when every source failed instead of flashing 0.
    if (failures === 2)
      return false;

    unreadCount.value = total;
    return true;
  };

  // 更新未读数量（减少）
  const decreaseUnreadCount = (count: number = 1) => {
    unreadCount.value = Math.max(0, unreadCount.value - count);
  };

  // 更新未读数量（增加）
  const increaseUnreadCount = (count: number = 1) => {
    unreadCount.value += count;
  };

  // 重置未读数量
  const resetUnreadCount = () => {
    unreadCount.value = 0;
  };

  return {
    unreadCount,
    fetchUnreadCount,
    decreaseUnreadCount,
    increaseUnreadCount,
    resetUnreadCount,
  };
});
